import { FunctionComponent, useMemo } from "react";

import { faColumns3, faSliders } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { TableColumn } from "@/components/Table/Table";

import { Order, SortParams } from "@/types/types";

import { SortByDropdown } from "../SortByDropdown/SortByDropdown";
import { SortOption } from "../SortByDropdown/sort-by-dropdown.types";
import { IFilterWithValues } from "../FilterModal";

interface UnassignedTableActionsProps {
	ts: (key: string) => string;
	/** All table columns (visible + sortable ones feed the Sort by dropdown). */
	columns: TableColumn[];
	sortBy: string;
	sortOrder: Order;
	onSort: (params: SortParams) => void;
	onOpenColumns: () => void;
	filter?: IFilterWithValues;
	onOpenFilter: () => void;
}

/**
 * The L3 toolbar action group on the Lead List — Sort by | Columns | Filter (PREC-3544).
 * Extracted from {@link UnassignedTable} so the new Sort by control lives outside that file.
 * The former "Add" dropdown was dissolved (PREC-3703): Add Lead is the sticky row at the table's
 * bottom, Add Column the sticky "+" column, Add Source part of the header's Sources dropdown.
 */
export const UnassignedTableActions: FunctionComponent<UnassignedTableActionsProps> = ({
	ts,
	columns,
	sortBy,
	sortOrder,
	onSort,
	onOpenColumns,
	filter,
	onOpenFilter,
}) => {
	// Only currently visible, sortable columns feed the alphabetical section (changes with
	// column visibility). A column is sortable when it exposes an `orderTitle`.
	const sortableColumnOptions: SortOption[] = useMemo(
		() =>
			columns
				.filter((column) => column.show && column.orderTitle && typeof column.title === "string")
				.map((column) => ({
					orderTitle: column.orderTitle as string,
					title: column.title as string,
				})),
		[columns]
	);

	const filterCount =
		filter && Object.keys(filter).length > 0 ? ` (${Object.keys(filter).length})` : "";

	return (
		<div className="ml-auto flex items-center gap-2">
			<SortByDropdown
				columnOptions={sortableColumnOptions}
				onSort={onSort}
				sortBy={sortBy}
				sortOrder={sortOrder}
				testId="list-sort-by"
			/>
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				image={<Icon className="mr-2" icon={faColumns3} />}
				size={ButtonSize.S}
				title={ts("columns")}
				onClick={onOpenColumns}
			/>
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				image={<Icon className="mr-2" icon={faSliders} />}
				size={ButtonSize.S}
				title={`${ts("filter")}${filterCount}`}
				onClick={onOpenFilter}
			/>
		</div>
	);
};
